import React, { useState } from 'react';
import { addClasses, cn } from '#lib/helpers/class.js';
import { PlaceRow, NewPlaceRow, SortableItemRow } from './places-overview.jsx';
import Button from '#lib/components/button.jsx';
import { FiltersModal } from '#src/components/filters-modal.jsx';
import { ItemDetail } from './detail.jsx';
import { Icon } from '@iconify-icon/react';
import { AddRowButton } from '#@itinerary/planner/row.jsx';
import { NewPlaceModal } from '#components/new-place-modal.jsx';
import PlacesPopUp from '#components/places-popup.jsx';
import PlacesDnD from './places-dnd.jsx';
import { newId } from '#helpers/helpers.js';
import useAuth from '#lib/hooks/use-auth.jsx';
import toast from 'react-hot-toast';

export default function Places(props) {
    const {
        itineraryId,
        placeFiltersProps, // i filtri
        placesProps, // i places
        isPlacesCreating, // state della generazione dei places
        setIsPlacesCreating,
        updatePlaces, // mutazione per generazione places
        totalFilters, // tutte le tipologie di filtro
        plans, // i plans
        addPlanFromPlace, // per aggiungere un plan dal dettaglio place
        totalDays, // durata totale plan
        filtersProps, // props query
        hasError,
        handleError,
        setUnsavedChanges,
        formData,
        saveItinerary,
        language
    } = props;
    const [selectedPlaceName, setSelectedPlaceName] = useState('');
    const [selectedPlace, setSelectedPlace] = useState('');
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [showPopUp, setShowPopUp] = useState(false);
    const popUpOptions = ['info', 'suggestions'];
    // MANAGE FILTERS MODAL
    const [filtersModal, setFiltersModal] = useState(false);
    const [newPlaceModal, setNewPlaceModal] = useState(false);

    const { placeFilters, editFilter } = placeFiltersProps;
    const {
        places,
        deleteLocation,
        addLocation,
        addPlace,
        deletePlace: _deletePlace,
        movePlace,
        editPlace,
        moveGallery,
        editHours,
        addPeriod,
        lockPlace,
        selectAllPlaces,
        deletePeriod
    } = placesProps;
    const deletePlace = React.useCallback(
        (name, type, id) => {
            _deletePlace(name, type, id);
            const placeIndex = places.findIndex((f) => f.name === name);
            const placeFilterIndex = placeFilters.findIndex((f) => f.name === name);
            const filterName = places[placeIndex].suggestions.find((el) => el.id === id)?.filterName;
            const filterId = placeFilters[placeFilterIndex].filters?.find(
                (f) => f.type === type || f.name === filterName
            )?.id;
            if (
                !(places[placeIndex].info.filter((x) => x.filterId === filterId).length - 1) ||
                !(places[placeIndex].suggestions.filter((x) => x.filterId === filterId).length - 1)
            ) {
                editFilter(placeIndex, type, filterId);
            }
        },
        [places, placeFilters]
    );
    const {
        meta: { data: userData }
    } = useAuth();
    const cantModify = !userData.mainUser && formData.isBlocked;

    const handleDeletePlace = (name, type, id) => {
        if (cantModify) return toast.error(_t.global.missingPermissions());
        const confirm = window.confirm(_t.global.confirmDelete());
        if (confirm) {
            deletePlace(name, type, id);
        }
    };

    //MANAGE DETAIL
    const Detail = (() => {
        if (selectedItemId) {
            const info = places.find((r) => r.name === selectedPlaceName)?.info.find((i) => i.id === selectedItemId);
            if (info) {
                return (
                    <>
                        <ItemDetail
                            key={info.id}
                            location={selectedPlaceName}
                            itemType="info"
                            data={info}
                            onEdit={editPlace}
                            onCreate={addPlanFromPlace}
                            onLock={lockPlace}
                            totalDays={totalDays}
                            plans={plans}
                            handleError={handleError}
                            hasError={hasError?.filter((v) => v.place === info.id)}
                            setUnsavedChanges={setUnsavedChanges}
                        />
                    </>
                );
            }

            const sugg = places
                .find((r) => r.name === selectedPlaceName)
                ?.suggestions.find((i) => i.id === selectedItemId);
            if (sugg)
                return (
                    <>
                        <ItemDetail
                            key={sugg.id}
                            location={selectedPlaceName}
                            itemType="suggestions"
                            data={sugg}
                            onAddPeriod={addPeriod}
                            onCreate={addPlanFromPlace}
                            onDeletePeriod={deletePeriod}
                            onEdit={editPlace}
                            onMoveGallery={moveGallery}
                            onEditHours={editHours}
                            onLock={lockPlace}
                            totalDays={totalDays}
                            totalFilters={totalFilters}
                            plans={plans}
                            handleError={handleError}
                            hasError={hasError?.filter((v) => v.place === sugg.id)}
                            setUnsavedChanges={setUnsavedChanges}
                        />
                    </>
                );
        }
        return null;
    })();

    return (
        <React.Fragment>
            <div className="w-full">
                <div
                    className={cn(
                        'col-span-full h-min-12 h-auto py-1 flex items-center border-b border-neutral-200',
                        (!placeFilters || placeFilters.every((el) => !el.name)) && 'bg-neutral-50'
                    )}
                >
                    {placeFilters && placeFilters.some((el) => el.name) ? (
                        <div className="w-full overflow-hidden text-sm font-medium text-ellipsis whitespace-nowrap text-neutral-500">
                            {placeFilters.map((pf, pfIdx) => {
                                return pf.name ? (
                                    <span key={pfIdx} className="mr-2">
                                        {pf.name}: info[{pf.filters?.some((f) => f.type === 'info') ? '+' : '-'}],
                                        suggestions[{pf.filters?.some((f) => f.type === 'suggestion') ? '+' : '-'}];
                                    </span>
                                ) : null;
                            })}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center w-full h-full">
                            <span className="text-sm italic hyphens-auto text-neutral-400" lang="en">
                                {_t.edit.places.noPlacesDisplayInfo()}
                            </span>
                        </div>
                    )}

                    <Button
                        onClick={() => {
                            if (!isPlacesCreating) {
                                setFiltersModal((state) => !state);
                            }
                        }}
                        className={addClasses('ml-auto')}
                        disabled={cantModify}
                    >
                        <div className="flex items-center gap-x-1">
                            <Icon
                                icon="line-md:cog-loop"
                                width="1.2rem"
                                height="1.2rem"
                                className={cn(isPlacesCreating && 'text-neutral-400')}
                            />
                            <span className={cn(isPlacesCreating && 'text-neutral-400')}>
                                {_t.edit.places.managePlaces()}
                            </span>
                        </div>
                    </Button>
                </div>
                <div className="relative grid grid-cols-1 divide-x xl:grid-cols-12 col-span-full divide-neutral-200">
                    <div className="grid col-span-full xl:col-span-6">
                        <div className="grid" style={{ maxHeight: '0%' }}>
                            {places && places.length > 0 && (
                                <div
                                    className={cn(
                                        'flex flex-col col-span-full gap-5 mt-2',
                                        isPlacesCreating && 'opacity-30 pointer-events-none'
                                    )}
                                >
                                    {places?.map((place, placeIndex) => {
                                        const infoRes = place?.info;
                                        const suggestionRes = place?.suggestions;

                                        return (
                                            <PlaceRow
                                                key={`pl-${placeIndex}`}
                                                index={placeIndex}
                                                onSelectAll={() => {
                                                    const { info, suggestions } = place;
                                                    const isSomeUnchecked =
                                                        info.some((item) => !item.checked) ||
                                                        suggestions.some((item) => !item.checked);
                                                    selectAllPlaces(place.name, isSomeUnchecked);
                                                }}
                                                onSelectPlace={() => {
                                                    if (selectedPlace === place.name) setSelectedPlace('');
                                                    else setSelectedPlace(place.name);
                                                }}
                                                onEdit={editPlace}
                                                onLock={lockPlace}
                                                onDeletePlace={handleDeletePlace}
                                                onDeleteLocation={(name) => {
                                                    if (cantModify) return toast.error(_t.global.missingPermissions());
                                                    deleteLocation(name);
                                                    const filteredPlaces = places.filter((p) => p.name !== name);
                                                    const filteredPlaceFilters = placeFilters.filter(
                                                        (p) => p.name !== name
                                                    );
                                                    saveItinerary({
                                                        data: formData,
                                                        newPlaces: filteredPlaces,
                                                        newPlaceFilters: filteredPlaceFilters,
                                                        hidden: true
                                                    });
                                                }}
                                                isPlaceSelected={selectedPlace && selectedPlace === place.name}
                                                name={place.name}
                                                place={place}
                                                plans={plans}
                                            >
                                                <div>
                                                    {infoRes && infoRes.length > 0 && (
                                                        <>
                                                            <h3 className="font-semibold text-neutral-900">
                                                                {_t.edit.places.info()}
                                                            </h3>
                                                            <PlacesDnD
                                                                location={place}
                                                                selectedPlaceId={selectedItemId}
                                                                placeType="info"
                                                                movePlace={movePlace}
                                                            >
                                                                {infoRes?.map((el) => {
                                                                    const selected =
                                                                        selectedItemId === el.id &&
                                                                        selectedPlaceName === place.name;
                                                                    const filterKey = totalFilters?.find(
                                                                        (f) => f.id === el.filterId
                                                                    )?.name;

                                                                    const infoName = el.name
                                                                        ? el.name
                                                                        : filterKey
                                                                        ? _t.edit.filters[filterKey]()
                                                                        : _t.edit.places.info();

                                                                    return (
                                                                        <SortableItemRow
                                                                            key={el.id}
                                                                            id={el.id}
                                                                            itemId={el.id}
                                                                            className={
                                                                                el.name === ''
                                                                                    ? '!text-neutral-400'
                                                                                    : ''
                                                                            }
                                                                            type="info"
                                                                            placeName={place.name}
                                                                            name={infoName}
                                                                            onSelect={(id) => {
                                                                                setSelectedPlaceName(place.name);
                                                                                setSelectedItemId(id);
                                                                            }}
                                                                            onEdit={editPlace}
                                                                            onLock={lockPlace}
                                                                            checked={el.checked}
                                                                            locked={el.locked}
                                                                            onDelete={() => {
                                                                                if (!el.locked)
                                                                                    handleDeletePlace(
                                                                                        place.name,
                                                                                        'info',
                                                                                        el.id
                                                                                    );
                                                                            }}
                                                                            isPlaceSelected={
                                                                                selectedPlace &&
                                                                                selectedPlace === place.name
                                                                            }
                                                                            selected={selected}
                                                                            hasError={hasError?.filter(
                                                                                (err) => err.place === el.id
                                                                            )}
                                                                            plans={plans}
                                                                        />
                                                                    );
                                                                })}
                                                            </PlacesDnD>
                                                        </>
                                                    )}

                                                    {suggestionRes && suggestionRes.length > 0 && (
                                                        <>
                                                            <h3 className="font-semibold text-neutral-900">
                                                                {_t.edit.filters.suggestions()}
                                                            </h3>
                                                            <PlacesDnD
                                                                location={place}
                                                                selectedPlaceId={selectedItemId}
                                                                placeType="suggestions"
                                                                movePlace={movePlace}
                                                            >
                                                                {suggestionRes
                                                                    .sort((a, b) => a.distance - b.distance)
                                                                    .map((s) => {
                                                                        const suggName = s.name
                                                                            ? s.name
                                                                            : _t.edit.filters.suggestions();

                                                                        const selected =
                                                                            selectedItemId === s.id &&
                                                                            selectedPlaceName === place.name;
                                                                        return (
                                                                            <SortableItemRow
                                                                                key={s.id}
                                                                                id={s.id}
                                                                                itemId={s.id}
                                                                                importId={s.importId ?? null}
                                                                                hasError={hasError?.filter(
                                                                                    (err) => err.place === s.id
                                                                                )}
                                                                                className={
                                                                                    s.name === ''
                                                                                        ? '!text-neutral-400'
                                                                                        : ''
                                                                                }
                                                                                type="suggestions"
                                                                                placeName={place.name}
                                                                                plans={plans}
                                                                                name={suggName}
                                                                                category={s.filterName}
                                                                                onSelect={(id) => {
                                                                                    setSelectedPlaceName(place.name);
                                                                                    setSelectedItemId(id);
                                                                                }}
                                                                                onLock={lockPlace}
                                                                                onEdit={editPlace}
                                                                                checked={s.checked}
                                                                                locked={s.locked}
                                                                                onDelete={() => {
                                                                                    if (!s.locked)
                                                                                        handleDeletePlace(
                                                                                            place.name,
                                                                                            'suggestions',
                                                                                            s.id
                                                                                        );
                                                                                }}
                                                                                isPlaceSelected={
                                                                                    selectedPlace &&
                                                                                    selectedPlace === place.name
                                                                                }
                                                                                selected={selected}
                                                                            />
                                                                        );
                                                                    })}
                                                            </PlacesDnD>
                                                        </>
                                                    )}
                                                    <div className="flex justify-center w-full p-2">
                                                        <AddRowButton
                                                            onClick={() => {
                                                                setShowPopUp(true);
                                                                setSelectedPlaceName(place.name);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </PlaceRow>
                                        );
                                    })}
                                </div>
                            )}
                            <PlacesPopUp
                                types={popUpOptions}
                                isOpen={showPopUp}
                                onClose={() => setShowPopUp(false)}
                                addPlace={(type) => {
                                    const id = newId();
                                    addPlace(selectedPlaceName, type, id);
                                    setSelectedItemId(id);
                                }}
                            />
                            <NewPlaceRow
                                className={addClasses(cn(isPlacesCreating && 'opacity-30 pointer-events-none'))}
                                onClick={() => setNewPlaceModal(true)}
                            />
                        </div>
                    </div>
                    <div
                        className={cn(
                            'col-span-full xl:col-span-6 bg-neutral-100 xl:bg-white mb-4 h-full',
                            isPlacesCreating && 'opacity-30 pointer-events-none',
                            !Detail && 'hidden xl:block'
                        )}
                    >
                        {places &&
                            places.length > 0 &&
                            (Detail ? (
                                Detail
                            ) : (
                                <section className="relative hidden h-full xl:block">
                                    <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full bg-neutral-50">
                                        <p className="w-2/3 italic text-center text-neutral-400">
                                            {_t.edit.places.placeDetailText()}
                                        </p>
                                    </div>
                                </section>
                            ))}
                    </div>
                    {isPlacesCreating && (
                        <div className="min-h-[10rem] absolute top-0 left-0 w-full h-full flex flex-col justify-center items-center border-none bg-[#dddddd22]">
                            <Icon
                                icon="svg-spinners:gooey-balls-2"
                                width="4rem"
                                height="4rem"
                                className="mr-2 text-primary-400"
                            />
                            <span className="text-neutral-600">{_t.edit.places.creatingPlaces()}</span>
                        </div>
                    )}
                </div>
            </div>
            {/* // MODALS */}
            <NewPlaceModal
                isOpen={newPlaceModal}
                onClose={() => setNewPlaceModal((state) => !state)}
                onSubmit={(placeName) => addLocation(placeName)}
            />
            <FiltersModal
                itineraryId={itineraryId}
                isOpen={filtersModal}
                onClose={() => setFiltersModal((state) => !state)}
                isCreating={isPlacesCreating}
                handleIsCreating={setIsPlacesCreating}
                isGenerateError={filtersProps?.isError}
                places={places}
                deletePlace={deletePlace}
                updatePlaces={updatePlaces}
                plans={plans}
                totalFilters={totalFilters?.filter((v) => !v.hidden)}
                placeFiltersProps={placeFiltersProps} // TODO: destrutturare props domani
                formData={formData}
                saveItinerary={saveItinerary}
                language={language}
            />
        </React.Fragment>
    );
}
